import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API, TOKEN_KEY } from "@/lib/api";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { STUDENT_NAV } from "@/pages/student/StudentHome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, Loader2 } from "lucide-react";

const SUGGESTIONS = [
  "Why do my skills fit my chosen field?",
  "What's the future job outlook for my field?",
  "What are my top 3 skills to strengthen?",
  "Which other career fields should I explore?",
];

export default function CoachPage() {
  const [params] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/coach/history").then(({ data }) => setMessages(data.map((m) => ({ role: m.role, content: m.content }))));
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);

    const scenario = params.get("program") ? { program_id: params.get("program") } : null;
    try {
      const res = await fetch(`${API}/coach`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
        body: JSON.stringify({ message: msg, scenario }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = JSON.parse(line.slice(6));
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.delta };
              return copy;
            });
          } else if (payload.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: "Sorry — the coach is unavailable right now. " + payload.error };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Connection error. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <DashboardShell title="AI Skills Coach"
      subtitle="Understand your fit, plan your growth, and narrate what-if scenarios"
      nav={STUDENT_NAV}>
      <div className="flex h-[calc(100vh-220px)] flex-col rounded-2xl border border-slate-200 bg-white card-elevate">
        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-6" data-testid="coach-messages">
          {messages.length === 0 && (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#003057]/6">
                <Sparkles className="h-7 w-7 text-[#78be21]" />
              </div>
              <p className="mt-4 font-heading text-lg font-bold text-[#003057]">Your GPA+ Skills Coach</p>
              <p className="mt-1 max-w-md text-sm text-slate-500">Ask about your career fit, growth areas, or a what-if degree change. Powered by Claude Sonnet 5 with your real TSB profile.</p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {SUGGESTIONS.map((s, i) => (
                  <button key={s} data-testid={`coach-suggestion-${i}`} onClick={() => send(s)}
                    className="rounded-full border border-slate-200 bg-slate-50 px-3.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-[#78be21] hover:text-[#003057]">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                m.role === "user" ? "bg-[#003057] text-white" : "bg-slate-100 text-slate-800"}`}>
                {m.content || (streaming && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin" /> : "")}
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-slate-200 p-4">
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
            <Input data-testid="coach-input" value={input} onChange={(e) => setInput(e.target.value)}
              placeholder="Ask your Skills Coach…" disabled={streaming} className="rounded-full" />
            <Button type="submit" data-testid="coach-send" disabled={streaming || !input.trim()}
              className="rounded-full bg-[#78be21] px-5 font-semibold text-[#00243f] hover:bg-[#6aac1c]">
              {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </form>
        </div>
      </div>
    </DashboardShell>
  );
}
